import React, { useEffect, useRef, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { translatePromptText } from "./translatePromptText";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { createBrowserTranslator, TranslatorAPI } from "./browserTranslator";

/** Chrome-only, read-only translation. No server calls or draft writes. */
export default function PromptTranslationDialog({
  text,
  disabled,
}: {
  text: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [translation, setTranslation] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const snapshot = useRef("");
  const cache = useRef<{ text: string; translation: string } | undefined>(
    undefined,
  );
  const sequence = useRef(0);
  const active = useRef<(() => void) | undefined>(undefined);
  const cancel = () => {
    sequence.current++;
    active.current?.();
    active.current = undefined;
  };
  useEffect(() => () => cancel(), []);

  const translate = async (source: string) => {
    cancel();
    const id = sequence.current;
    setError("");
    setTranslation("");
    setStatus("");
    if (cache.current?.text === source) {
      setTranslation(cache.current.translation);
      return;
    }
    const api = (window as unknown as { Translator?: TranslatorAPI })
      .Translator;
    if (!api) {
      setError(
        "当前浏览器未提供内置翻译接口，请使用支持该功能的桌面版 Chrome，并通过 HTTPS 或 localhost 打开页面。",
      );
      return;
    }
    setStatus("正在准备 Chrome 翻译，首次使用可能需要下载语言包…");
    let release: (() => void) | undefined;
    try {
      // Start during the click gesture: Chrome may require user activation to download.
      const translator = await createBrowserTranslator((loaded) => {
        if (id === sequence.current)
          setStatus(`正在下载 Chrome 翻译语言包：${Math.round(loaded * 100)}%`);
      });
      let released = false;
      release = () => {
        if (!released) {
          released = true;
          translator.destroy();
        }
      };
      if (id !== sequence.current) return;
      active.current = release;
      setStatus("正在翻译正文…");
      const result = await translatePromptText(
        source,
        (fragment) => translator.translate(fragment),
        () => id !== sequence.current,
        (line, total) => setStatus(`正在翻译正文（${line}/${total}）…`),
      );
      if (id !== sequence.current) return;
      if (!result.trim()) throw new Error("Empty translation");
      cache.current = { text: source, translation: result };
      setTranslation(result);
    } catch {
      if (id === sequence.current)
        setError(
          "Chrome 翻译暂时不可用。请检查语言包下载或浏览器设置后重试，原文未改动。",
        );
    } finally {
      release?.();
      if (active.current === release) active.current = undefined;
      if (id === sequence.current) setStatus("");
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled || !text.trim()}
        onClick={() => {
          snapshot.current = text;
          setOpen(true);
          void translate(text);
        }}
      >
        <Languages className="mr-1 h-4 w-4" />
        查看翻译正文
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          setOpen(value);
          if (!value) cancel();
        }}
      >
        <DialogContent className="flex max-h-[80vh] max-w-3xl flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>正文中文翻译</DialogTitle>
            <DialogDescription>
              使用 Chrome 本地翻译，仅供阅读，不会保存草稿或新增修改记录。
            </DialogDescription>
          </DialogHeader>
          {status ? (
            <p role="status" className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {status}
            </p>
          ) : error ? (
            <div>
              <p role="alert" className="mb-3 text-sm text-destructive">
                {error}
              </p>
              <Button
                variant="outline"
                onClick={() => void translate(snapshot.current)}
              >
                重试翻译
              </Button>
            </div>
          ) : (
            <div
              aria-label="翻译正文"
              lang="zh-CN"
              translate="no"
              className="min-h-0 overflow-y-auto whitespace-pre-wrap break-words text-sm leading-7"
            >
              {translation}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
